import { useState, useRef, useEffect } from 'react';
import { dgiiApi } from '../../api/dgii';
import { useDgiiCarga } from '../../contexts/DgiiCargaContext';
import {
	DocumentArrowUpIcon,
	XMarkIcon,
	ClockIcon,
	CameraIcon,
	CreditCardIcon,
	BanknotesIcon,
	DocumentCheckIcon,
	ArrowsRightLeftIcon,
	DocumentTextIcon,
	Squares2X2Icon,
} from '@heroicons/react/24/outline';
import PendingInvoicesSection from './PendingInvoicesSection';

const METODOS_PAGO = [
	{ value: '1', label: 'Efectivo', icon: BanknotesIcon },
	{ value: '2', label: 'Cheques/Transferencias/Depósito', icon: DocumentCheckIcon },
	{ value: '3', label: 'Tarjeta Crédito/Débito', icon: CreditCardIcon },
	{ value: '4', label: 'Compra a Crédito', icon: CreditCardIcon },
	{ value: '5', label: 'Permuta', icon: ArrowsRightLeftIcon },
	{ value: '6', label: 'Notas de Crédito', icon: DocumentTextIcon },
	{ value: '7', label: 'Mixto', icon: Squares2X2Icon },
];

export default function SingleInvoiceUploader({ onUploadComplete, empresaSeleccionada, disabled }) {
	const { colaTimestampInicio, setColaTimestampInicio } = useDgiiCarga();
	const tipoFormulario = '606'; // Solo trabajamos con formulario 606
	const [metodoPago, setMetodoPago] = useState('1'); // Por defecto "Efectivo" (Norma 07-18)
	const [showMetodoPago, setShowMetodoPago] = useState(true); // true = mostrar selector, false = mostrar formulario de carga
	const [file, setFile] = useState(null);
	const [filePreview, setFilePreview] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [error, setError] = useState(null);
	const [isDragging, setIsDragging] = useState(false);
	const [timestampInicioSesion, setTimestampInicioSesion] = useState(null);
	const fileInputRef = useRef(null);
	const cameraInputRef = useRef(null);
	const dropZoneRef = useRef(null);

	// Restaurar timestamp de cola al volver de otra ruta para que se muestren las cards de estado
	useEffect(() => {
		if (colaTimestampInicio != null && timestampInicioSesion == null) {
			setTimestampInicioSesion(colaTimestampInicio);
		}
	}, [colaTimestampInicio]);

	// Solo mostrar selector de método de pago si es Form 606
	const requiereMetodoPago = tipoFormulario === '606';
	
	// Deshabilitar carga si no hay empresa seleccionada
	const empresaDeshabilitada = !empresaSeleccionada;

	const handleMetodoPagoSelect = (metodo) => {
		setMetodoPago(metodo);
		setShowMetodoPago(false); // Ocultar selector y mostrar opciones de carga
	};

	const handleFileSelect = (selectedFile) => {
		// Prevenir selección si no hay empresa
		if (empresaDeshabilitada) {
			setError('Debes seleccionar una empresa antes de cargar archivos');
			return;
		}
		
		// Solo permitir un archivo a la vez
		if (selectedFile && selectedFile.length > 0) {
			const file = selectedFile[0];
			setFile(file);
			setError(null);
			
			// Crear preview si es una imagen
			if (file.type.startsWith('image/')) {
				const reader = new FileReader();
				reader.onloadend = () => {
					setFilePreview(reader.result);
				};
				reader.readAsDataURL(file);
			} else {
				setFilePreview(null);
			}
		}
	};

	const handleInputChange = (e) => {
		if (e.target.files && e.target.files.length > 0) {
			handleFileSelect(e.target.files);
		}
		// Limpiar el input para permitir tomar otra foto del mismo archivo
		if (e.target === cameraInputRef.current) {
			e.target.value = '';
		}
	};

	const handleDragOver = (e) => {
		if (empresaDeshabilitada) {
			return; // No permitir drag si no hay empresa
		}
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(true);
	};

	const handleDragLeave = (e) => {
		if (empresaDeshabilitada) {
			return;
		}
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);
	};

	const handleDrop = (e) => {
		if (empresaDeshabilitada) {
			e.preventDefault();
			e.stopPropagation();
			setError('Debes seleccionar una empresa antes de cargar archivos');
			return;
		}
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);

		if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
			handleFileSelect(e.dataTransfer.files);
		}
	};

	const handleUpload = async () => {
		if (!file) {
			setError('Por favor selecciona un archivo');
			return;
		}

		if (requiereMetodoPago && !metodoPago) {
			setError('Por favor selecciona un método de pago');
			return;
		}

		setUploading(true);
		setError(null);

		if (!empresaSeleccionada) {
			setError('Por favor selecciona una empresa antes de subir la factura');
			setUploading(false);
			return;
		}

		// Cada carga debe tener su propio timestamp de inicio (estadísticas por carga)
		const ts = Date.now() / 1000; // Timestamp en segundos
		setTimestampInicioSesion(ts);
		setColaTimestampInicio(ts); // Persistir en context para que las cards sigan visibles al cambiar menú

		try {
			const formData = new FormData();
			formData.append('archivo', file);
			formData.append('nombre_original', file.name);
			formData.append('tipo_formulario', tipoFormulario);
			formData.append('tipo_carga', 'individual'); // Marcar como carga individual
			// CRÍTICO: Incluir empresa seleccionada como número
			formData.append('empresa_id', String(empresaSeleccionada)); // Convertir a string para FormData
			
			// Incluir método de pago si es Form 606
			if (requiereMetodoPago && metodoPago) {
				formData.append('metodo_pago', metodoPago);
			}
			
			// No enviar formulario_id - se creará automáticamente durante el procesamiento
			
			const result = await dgiiApi.uploadFactura(null, formData);
			
			// Resetear estado
			setFile(null);
			setFilePreview(null);
			setShowMetodoPago(true); // Volver a mostrar selector de método de pago para la próxima factura
			// No resetear metodoPago, mantener el seleccionado
			if (fileInputRef.current) {
				fileInputRef.current.value = '';
			}
			if (cameraInputRef.current) {
				cameraInputRef.current.value = '';
			}
			
			// Llamar al callback después de un pequeño delay para asegurar que el backend procesó
			setTimeout(() => {
				if (onUploadComplete) {
					onUploadComplete();
				}
			}, 500);
		} catch (err) {
			console.error('Error subiendo archivo:', err);
			setError(err.response?.data?.detail || err.message || 'Error al subir factura');
		} finally {
			setUploading(false);
		}
	};

	const formatFileSize = (bytes) => {
		if (bytes === 0) return '0 Bytes';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
	};

	// Mostrar primero el selector de método de pago o el formulario de carga, luego las pendientes
	return (
		<div className="space-y-6">
			{/* Si es Form 606 y aún no se ha seleccionado método de pago, mostrar selector */}
			{requiereMetodoPago && showMetodoPago ? (
				<div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
					<h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">
						Selecciona Método de Pago
					</h3>
					<p className="text-sm text-gray-600 mb-4">
						Por favor selecciona el método de pago antes de cargar la factura.
					</p>
					<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
						{METODOS_PAGO.map((metodo) => {
							const Icon = metodo.icon;
							return (
								<button
									key={metodo.value}
									type="button"
									onClick={() => handleMetodoPagoSelect(metodo.value)}
									className={`p-2 sm:p-4 rounded-lg border-2 transition-all ${
										metodoPago === metodo.value
											? 'border-primary bg-primary/10 text-primary font-medium'
											: 'border-gray-200 bg-white text-gray-700 hover:border-primary/50 hover:bg-primary/5'
									}`}
								>
									<div className="flex flex-col items-center gap-1 sm:gap-2">
										<Icon className="h-5 w-5 sm:h-7 sm:w-7 shrink-0" />
										<span className="text-[10px] leading-tight sm:text-sm text-center w-full">
											{metodo.label}
										</span>
									</div>
								</button>
							);
						})}
					</div>
				</div>
			) : (
				/* Una vez seleccionado el método de pago (o si no es Form 606), mostrar opciones de carga */
				<div className="bg-white rounded-lg shadow-md p-3 sm:p-4 md:p-6">
			<div className="flex items-center justify-between mb-3 sm:mb-4">
				<h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-800">
					Factura Individual
				</h3>
				{requiereMetodoPago && (
					<button
						type="button"
						onClick={() => {
							setShowMetodoPago(true);
							setFile(null);
							setFilePreview(null);
						}}
						className="text-xs sm:text-sm text-primary hover:text-primary/80 font-medium"
					>
						Cambiar método de pago
					</button>
				)}
			</div>
			
			{requiereMetodoPago && (
				<div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-primary/5 rounded-lg">
					<p className="text-xs sm:text-sm text-gray-700">
						<span className="font-medium">Método de pago seleccionado:</span>{' '}
						{METODOS_PAGO.find(m => m.value === metodoPago)?.label || 'N/A'}
					</p>
				</div>
			)}
			
			{/* Zona de carga */}
			<div
				ref={dropZoneRef}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
				className={`border-2 border-dashed rounded-lg p-4 sm:p-6 md:p-8 text-center transition-all ${
					empresaDeshabilitada
						? 'border-gray-200 bg-gray-100 opacity-60 cursor-not-allowed'
						: isDragging
						? 'border-blue-500 bg-blue-50'
						: 'border-gray-300 hover:border-blue-400 bg-gray-50 active:bg-gray-100'
				}`}
			>
				<DocumentArrowUpIcon className={`mx-auto h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 mb-3 sm:mb-4 transition-colors ${
					empresaDeshabilitada ? 'text-gray-300' : isDragging ? 'text-blue-500' : 'text-gray-400'
				}`} />
				
				{/* Botones de acción */}
				<div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center items-center mb-3 sm:mb-4">
					<label className={empresaDeshabilitada ? 'cursor-not-allowed' : 'cursor-pointer'}>
						<button
							type="button"
							onClick={() => {
								if (empresaDeshabilitada) {
									setError('Debes seleccionar una empresa antes de cargar archivos');
									return;
								}
								fileInputRef.current?.click();
							}}
							disabled={empresaDeshabilitada}
							className={`inline-flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-medium rounded-lg transition-opacity touch-manipulation shadow-sm w-full sm:w-auto ${
								empresaDeshabilitada
									? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
									: 'bg-primary text-white hover:opacity-90 active:opacity-80'
							}`}
						>
							<DocumentArrowUpIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
							<span className="whitespace-nowrap">Seleccionar Archivo</span>
						</button>
						<input
							ref={fileInputRef}
							type="file"
							accept=".pdf,.png,.jpg,.jpeg,.tiff,.bmp"
							onChange={handleInputChange}
							disabled={empresaDeshabilitada}
							className="hidden"
						/>
					</label>
					
					<label className={empresaDeshabilitada ? 'cursor-not-allowed' : 'cursor-pointer'}>
						<button
							type="button"
							onClick={() => {
								if (empresaDeshabilitada) {
									setError('Debes seleccionar una empresa antes de cargar archivos');
									return;
								}
								cameraInputRef.current?.click();
							}}
							disabled={empresaDeshabilitada}
							className={`inline-flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-medium rounded-lg transition-opacity touch-manipulation shadow-sm w-full sm:w-auto ${
								empresaDeshabilitada
									? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
									: 'bg-secondary text-white hover:opacity-90 active:opacity-80'
							}`}
						>
							<CameraIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
							<span className="whitespace-nowrap">Tomar Foto</span>
						</button>
						<input
							ref={cameraInputRef}
							type="file"
							accept="image/*"
							capture="environment"
							onChange={handleInputChange}
							disabled={empresaDeshabilitada}
							className="hidden"
						/>
					</label>
				</div>
				
				{empresaDeshabilitada ? (
					<p className="text-xs sm:text-sm text-gray-500 mb-1 italic">
						Selecciona una empresa para habilitar la carga de archivos
					</p>
				) : (
					<>
						<p className="text-xs sm:text-sm text-gray-600 mb-1">
							<span className="hidden sm:inline">También puedes arrastrar y soltar archivo aquí</span>
							<span className="sm:hidden">o toca para seleccionar</span>
						</p>
						<p className="text-xs text-gray-500">
							PDF, PNG, JPG, JPEG, TIFF, BMP
						</p>
					</>
				)}
			</div>

			{/* Archivo seleccionado */}
			{file && (
				<div className="mt-4 bg-gray-50 p-3 rounded-lg border border-gray-200">
					<div className="flex items-start space-x-3">
						{/* Preview de imagen */}
						{filePreview && (
							<div className="flex-shrink-0">
								<img
									src={filePreview}
									alt="Preview"
									className="h-20 w-20 object-cover rounded-lg border border-gray-300"
								/>
							</div>
						)}
						
						{/* Información del archivo */}
						<div className="flex-1 min-w-0">
							<div className="flex items-center justify-between">
								<div className="flex-1 min-w-0 pr-2">
									<p className="text-xs sm:text-sm font-medium text-gray-800 truncate">
										{file.name}
									</p>
									<p className="text-xs text-gray-500 mt-0.5">
										{formatFileSize(file.size)}
									</p>
								</div>
								<button
									type="button"
									onClick={() => {
										setFile(null);
										setFilePreview(null);
									}}
									className="ml-2 text-red-600 hover:text-red-700 active:text-red-800 flex-shrink-0 p-1"
									title="Eliminar archivo"
								>
									<XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Mensaje de error */}
			{error && (
				<div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-xs sm:text-sm">
					{error}
				</div>
			)}

			{/* Botón de subida */}
			{file && (
				<button
					onClick={handleUpload}
					disabled={uploading || disabled}
					className="mt-4 w-full bg-secondary text-white py-3 sm:py-3.5 px-4 rounded-lg hover:opacity-90 active:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-opacity text-sm sm:text-base shadow-sm touch-manipulation flex items-center justify-center space-x-2"
					title={disabled ? 'Debes seleccionar una empresa primero' : ''}
				>
					{uploading ? (
						<>
							<ClockIcon className="h-5 w-5 animate-spin" />
							<span>Subiendo archivo...</span>
						</>
					) : (
						<>
							<DocumentArrowUpIcon className="h-5 w-5" />
							<span>Subir Factura</span>
						</>
					)}
				</button>
			)}
				</div>
			)}
			
			{/* Lista de pendientes individuales - debajo del método de pago */}
			<PendingInvoicesSection
				tipoCarga="individual"
				onProcessComplete={onUploadComplete}
				onRefresh={onUploadComplete}
				empresaId={empresaSeleccionada}
				timestampInicioSesion={timestampInicioSesion}
			/>
		</div>
	);
}

